import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import AdminSectionHeader from "../components/admin/layout/AdminSectionHeader";
import AdminSidebar from "../components/admin/layout/AdminSidebar";
import ClientHistorySection from "../components/admin/sections/ClientHistorySection";
import ComplexesDatabaseSection from "../components/admin/sections/ComplexesDatabaseSection";
import SipDispatcherSection from "../components/admin/sections/SipDispatcherSection";
import TransfersSection from "../components/admin/sections/TransfersSection";
import OperationsHistorySection from "../components/admin/sections/OperationsHistorySection";
import CitiesSection from "../components/admin/sections/CitiesSection";
import SipClientPricesSection from "../components/admin/sections/SipClientPricesSection";
import TelegramStatusSection from "../components/admin/telegram/TelegramStatusSection";
import MarketcallSection from "../components/admin/marketcall/MarketcallSection";
import { adminStyles as styles } from "../components/admin/shared/adminStyles";
import WorkspaceSelectionDrawer from "../components/admin/workspaceSelection/WorkspaceSelectionDrawer";
import {
  useWorkspaceSelection,
  WorkspaceSelectionProvider,
} from "../components/admin/workspaceSelection/WorkspaceSelectionContext";
import { SavedViewsProvider } from "../components/admin/savedViews/SavedViewsContext";
import { AdminDataRefreshProvider } from "../components/admin/dataRefresh/AdminDataRefreshContext";
import { WorkspaceSelectionModeProvider, useWorkspaceSelectionMode } from "../components/admin/workspaceSelection/WorkspaceSelectionModeContext";

const ADMIN_SECTIONS = [
  { key: "complexes", label: "База ЖК", description: "Поиск, фильтрация и пакетная работа с основными данными жилых комплексов." },
  { key: "sip", label: "Клиенты", description: "ЖК выбранного клиента, его состояние и общие пакетные инструменты." },
  { key: "transfers", label: "СИПы", description: "Сводная таблица клиентов, номеров перевода и СИПов." },
  { key: "prices", label: "Цены", description: "Матрица цен офферов клиентов по жилым комплексам." },
  { key: "history", label: "История", description: "История запуска и остановки клиентов на карте." },
  { key: "operations", label: "История операций", description: "Журнал пакетных изменений, частичных результатов и ошибок." },
  { key: "cities", label: "Города", description: "Справочник городов, координаты карты и порядок отображения." },
  { key: "telegram", label: "Telegram", description: "Источники Telegram, правила обработки, отправители и журнал событий." },
  { key: "marketcall", label: "Marketcall", description: "Кампании, привязки, автоматизация и диагностика Marketcall." },
];

function AdminComplexesPageContent({ theme, toggleTheme }) {
  const [activeSection, setActiveSection] = useState("complexes");
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);
  const [complexOpenTarget, setComplexOpenTarget] = useState(null);
  const handleComplexOpenTarget = useCallback(() => setComplexOpenTarget(null), []);
  const selectionButtonRef = useRef(null);
  const { items: selectionItems } = useWorkspaceSelection();
  const { activeGroup } = useWorkspaceSelectionMode();
  const modeSection = activeGroup === "complexes" ? "complexes" : activeGroup === "sipClients" ? "sip" : activeGroup === "transfers" ? "transfers" : null;
  useEffect(() => {
    if (modeSection) setActiveSection(modeSection);
  }, [modeSection]);
  useEffect(() => {
    const open = (event) => { setComplexOpenTarget(event.detail || null); setActiveSection("complexes"); };
    window.addEventListener("admin:open-complex", open);
    return () => window.removeEventListener("admin:open-complex", open);
  }, []);
  const activeSectionMeta =
    ADMIN_SECTIONS.find((section) => section.key === activeSection) || ADMIN_SECTIONS[0];

  const renderActiveSection = () => {
    if (activeSection === "telegram") return <TelegramStatusSection />;
    if (activeSection === "marketcall") return <MarketcallSection />;
    if (activeSection === "cities") return <CitiesSection />;
    if (activeSection === "sip") {
      return <SipDispatcherSection />;
    }

    if (activeSection === "transfers") {
      return <TransfersSection />;
    }
    if (activeSection === "prices") return <SipClientPricesSection />;

    if (activeSection === "history") {
      return <ClientHistorySection />;
    }

    if (activeSection === "operations") {
      return <OperationsHistorySection />;
    }

    return <ComplexesDatabaseSection openTarget={complexOpenTarget} onOpenTargetHandled={handleComplexOpenTarget} />;
  };

  return (
    <div className="admin-workspace" style={styles.page}>
      <div className="admin-workspace__shell" style={styles.shell}>
        <AdminSidebar
          sections={ADMIN_SECTIONS}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          theme={theme}
          toggleTheme={toggleTheme}
          selectionCount={selectionItems.length}
          onOpenSelection={() => setIsSelectionOpen(true)}
          selectionButtonRef={selectionButtonRef}
          selectionOpen={isSelectionOpen}
        />

        <main className="admin-workspace__content" style={styles.content}>
          <AdminSectionHeader title={activeSectionMeta.label} description={activeSectionMeta.description} />
          {renderActiveSection()}
        </main>
      </div>
      <WorkspaceSelectionDrawer
        isOpen={isSelectionOpen}
        onClose={() => setIsSelectionOpen(false)}
        returnFocusRef={selectionButtonRef}
      />
    </div>
  );
}

function AdminComplexesPage({ isAdmin, theme, toggleTheme }) {
  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <WorkspaceSelectionProvider>
      <AdminDataRefreshProvider>
        <WorkspaceSelectionModeProvider>
          <SavedViewsProvider>
            <AdminComplexesPageContent theme={theme} toggleTheme={toggleTheme} />
          </SavedViewsProvider>
        </WorkspaceSelectionModeProvider>
      </AdminDataRefreshProvider>
    </WorkspaceSelectionProvider>
  );
}

export default AdminComplexesPage;
